import React, { useEffect, useState, useRef } from "react";
import { supabase, fetchInChunks } from "../../supabaseClient";
import Button from "../Button";
import { Modal } from "../Modal";
import ShowAllTiles from "./ShowAllTiles";
import SearchBar from "../SearchBar";
import { FaCheckCircle } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import Swal from "sweetalert2";
import { DeleteObjectCommand, PutObjectCommand } from "@aws-sdk/client-s3";
import { useParams } from "react-router-dom"; // <-- ADDED for reading :id from URL
import { minioClient } from "../../minioClient";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../Components/currentUser";
import MovieTile from "../MovieTile/MovieTile";
import { FaEye } from "react-icons/fa";
import { RowFormSkeleton } from "../Skeleton";
import HighlightRowBuilder, {
  buildSlotKey,
  resolveHighlightUrl,
  safeJsonParseArray,
  LABEL_FIELD_KEYS,
  DEFAULT_LABEL_VALUES,
} from "./HighlightRowBuilder";

function RowForm({ setShowModal, fetchRows, editRow: editRowProp, searchRow }) {
  const [formData, setFormData] = useState({
    rowLayout: "",
    tileCategory: "",
    rowHeader: "",
    row_width: "",
    row_height: "",
    row_bg: "",
    row_icon: "",
    rowType: "",
    utm_source: "cloudtvos",
    utm_medium: "",
    utm_campaign: "",
  });
  const [Tiles, setTiles] = useState([]); // For dropdown suggestions, limited
  const [searchTerm, setSearchTerm] = useState("");
  const [showTilesModal, setShowTilesModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedTiles, setSelectedTiles] = useState([]);
  const dropdownRef = useRef(null);
  const utmMediumManualEdited = useRef(false);
  const pageContainerRef = useRef(null);
  const minioBaseUrl = "https://images.do.cloudwalker.tv/images/";
  const titleLogoBaseUrl = "https://images.do.cloudwalker.tv/images/";
  const { id } = useParams();
  const [editRow, setEditRow] = useState(editRowProp || null);
  const [isFormLoading, setIsFormLoading] = useState(!!(id || editRowProp));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showLayoutAlert, setShowLayoutAlert] = useState(false);
  const [layoutAlertMessage, setLayoutAlertMessage] = useState("");

  const [previewTile, setPreviewTile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [suitableTiles, setSuitableTiles] = useState([]);
  const [suitableImageField, setSuitableImageField] = useState("poster");
  const [suitableLoading, setSuitableLoading] = useState(false);
  const [suitablePage, setSuitablePage] = useState(0);
  const [suitableTotalCount, setSuitableTotalCount] = useState(0);
  const SUITABLE_PAGE_SIZE = 12;

  // "Highlight" row layout — base tiles pool their Background images into
  // slots, each optionally overlaid with a searched tile's image.
  const [baseTiles, setBaseTiles] = useState([]);
  const [highlightSlots, setHighlightSlots] = useState([]);
  // Lifted (not local to HighlightRowBuilder) so edit-load can set it to
  // match the saved row's actual field before the pooling effect re-runs —
  // otherwise it always resets to "poster" and silently replaces a saved
  // Portrait-based row's slots with a fresh, unconfigured Poster-based pool.
  const [baseImageField, setBaseImageField] = useState("poster");


  const layoutToImageField = {
    landscape: "poster",
    portrait: "portrait",
    longPortrait: "longPortrait",
    square: "square",
    background: "background",
    featureBanner: "featureBanner",
    heroBanner: "heroBanner",
    titleLogo: "titleLogo",
    banner: "banner",
    // landscapePoster: "landscapePoster",
    // mainBanner: "mainBanner",
  };

  const layoutToCategoryOptions = {
    landscape: ["poster", "mainBanner", "featureBanner", "heroBanner"],
    portrait: ["portrait"],
    longPortrait: ["longPortrait"],
    square: ["square"],
    background: ["background"],
    featureBanner: ["featureBanner"],
    heroBanner: ["heroBanner"],
    titleLogo: ["titleLogo"],
    banner: ["banner"],
    // landscapePoster: ["landscapePoster"],
    // mainBanner: ["mainBanner"],
  };

  const categoryToImageField = {
    landscape: "poster",
    poster: "poster",
    portrait: "portrait",
    longPortrait: "longPortrait",
    "long portrait": "longPortrait",
    square: "square",
    background: "background",
    featureBanner: "featureBanner",
    heroBanner: "heroBanner",
    titleLogo: "titleLogo",
    banner: "banner",
    // landscapePoster: "landscapePoster",
    // mainBanner: "mainBanner",
  };

  const fetchSuitableTiles = async (category, page = 0) => {
    const imageField = categoryToImageField[category] || layoutToImageField[category];
    if (!imageField) {
      setSuitableTiles([]);
      setSuitablePage(0);
      setSuitableTotalCount(0);
      return;
    }
    setSuitableTiles([]);
    setSuitableTotalCount(0);
    setSuitableImageField(imageField);
    setSuitableLoading(true);
    try {
      const from = page * SUITABLE_PAGE_SIZE;
      const to = from + SUITABLE_PAGE_SIZE - 1;

      const [{ data, error }, { count, error: countError }] = await Promise.all([
        supabase
          .from("tiles")
          .select("*")
          .not(imageField, "is", null)
          .neq(imageField, "[]")
          .neq(imageField, "")
          .order("created_at", { ascending: false })
          .range(from, to),
        page === 0
          ? supabase
            .from("tiles")
            .select("*", { count: "exact", head: true })
            .not(imageField, "is", null)
            .neq(imageField, "[]")
            .neq(imageField, "")
          : Promise.resolve({ count: null, error: null }),
      ]);

      if (error) throw error;
      if (countError) throw countError;

      const tiles = data || [];
      setSuitableTiles(tiles);
      if (page === 0) setSuitableTotalCount(count ?? tiles.length);
    } catch (err) {
      console.error("Error fetching suitable tiles:", err.message);
    } finally {
      setSuitableLoading(false);
    }
  };


  const [languageMap, setLanguageMap] = useState({});
  const [sourceMap, setSourceMap] = useState({});
  const [tileUtmParams, setTileUtmParams] = useState({});
  // { [tid]: { utm_source, utm_medium, utm_campaign } }
  const [expandedUtmTile, setExpandedUtmTile] = useState(null);

  const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  // tiles.source elements are sometimes the source row's uuid (needs a
  // sourceMap lookup) and sometimes the exact source NAME stored directly —
  // fall back to the raw value itself so name-stored tiles still resolve.
  const resolveSourceName = (idOrName) => {
    if (!idOrName) return "";
    return sourceMap[idOrName] || idOrName;
  };

  const isUtmEligibleSource = (sources) => {
    const list = Array.isArray(sources) ? sources : [];
    return list.some((id) => {
      const name = resolveSourceName(id).toLowerCase();
      const normalized = name.replace(/\s+/g, "");
      return (
        name.includes("sony") ||
        name.includes("sonylive") ||
        name.includes("hotstar") ||
        normalized.includes("sunnxt") ||
        name.includes("jojo")
      );
    });
  };

  const updateTileUtm = (tid, field, value) => {
    setTileUtmParams((prev) => ({
      ...prev,
      [tid]: { ...prev[tid], [field]: value },
    }));
  };

  // Per-tile override for which specific image (of possibly several uploaded
  // under this row's image field) shows for that tile on the page — keyed by tid.
  const [tileImageOverrides, setTileImageOverrides] = useState({});
  const [imagePickerTile, setImagePickerTile] = useState(null);

  // Drop overrides only for tiles that were selected and then got removed from
  // the row — NOT for tiles that were never added yet. Tiles can now be
  // previewed/pinned from the Suitable Tiles / Show All Tiles grids before
  // being added, so a blanket "not currently selected" wipe would discard
  // those pre-picks the moment an unrelated tile is added/removed.
  const prevSelectedTidsRef = useRef(new Set());
  useEffect(() => {
    const currentTids = new Set(selectedTiles.map((t) => t.tid));
    const prevTids = prevSelectedTidsRef.current;
    setTileImageOverrides((prev) => {
      const updated = { ...prev };
      prevTids.forEach((tid) => {
        if (!currentTids.has(tid)) delete updated[tid];
      });
      return updated;
    });
    prevSelectedTidsRef.current = currentTids;
  }, [selectedTiles]);

  const activeRowImageField =
    categoryToImageField[formData.tileCategory] || layoutToImageField[formData.rowLayout] || "poster";

  const getTileImageOptions = (tile, field) => {
    try {
      const raw = tile[field];
      if (!raw) return [];
      const parsed =
        typeof raw === "string"
          ? raw.trim().startsWith("[")
            ? JSON.parse(raw)
            : [raw]
          : Array.isArray(raw)
            ? raw
            : [];
      return parsed.filter(Boolean);
    } catch {
      return [];
    }
  };

  const resolveTileImageUrl = (src, field) => {
    if (!src) return null;
    const base = field === "titleLogo" ? titleLogoBaseUrl : minioBaseUrl;
    if (src.startsWith("http")) return src;
    const path = src.startsWith("tiles/") ? src : `tiles/${src}`;
    return `${base}${path}`;
  };




  function getAvailableImageTypes(tile) {
    const availableTypes = [];
    const imageFields = ['poster', 'portrait', 'longPortrait', 'square', 'background', 'featureBanner', 'heroBanner', 'titleLogo', 'banner'];

    imageFields.forEach(field => {
      try {
        const parsed = JSON.parse(tile[field] || '[]');
        if (Array.isArray(parsed) && parsed.length > 0) {
          availableTypes.push(field);
        }
      } catch {
        // Skip if parse fails
      }
    });

    return availableTypes;
  }

  function checkLayoutCompatibility(selectedTiles, selectedLayout, selectedCategory) {
    const incompatibleTiles = [];

    // Map layouts to required image fields
    const layoutToImageField = {
      "landscape": "poster",
      "portrait": "portrait",
      "longPortrait": "longPortrait",
      "square": "square",
      "background": "background",
      "featureBanner": "featureBanner",
      "heroBanner": "heroBanner",
      "titleLogo": "titleLogo",
      "banner": "banner",
     
    };

    const requiredField = (selectedCategory && categoryToImageField[selectedCategory])
      || layoutToImageField[selectedLayout];

    if (!requiredField || !selectedLayout || !selectedTiles.length) {
      return [];
    }

    selectedTiles?.forEach(tile => {
      // Check if the tile has the required image type
      const tileImageField = tile[requiredField];

      // Parse the JSON string to check if it has actual images
      let hasImages = false;
      try {
        const parsedImages = JSON.parse(tileImageField || '[]');
        hasImages = Array.isArray(parsedImages) && parsedImages.length > 0;
      } catch {
        hasImages = false;
      }

      if (!hasImages) {
        incompatibleTiles.push({
          id: tile.id,
          title: tile.title,
          requiredImageType: requiredField,
          currentImages: tileImageField || '[]',
          availableImages: getAvailableImageTypes(tile) // NEW: Show what IS available
        });
      }
    });

    return incompatibleTiles;
  }


  // Handle layout change with alert
  const handleLayoutChange = (e) => {
    const newLayout = e.target.value;

    // Check compatibility with current tiles
    if (selectedTiles.length > 0 && newLayout) {
      const incompatibleTiles = checkLayoutCompatibility(selectedTiles, newLayout, formData.tileCategory);

      if (incompatibleTiles.length > 0) {
        // Create alert message
        const tileNames = incompatibleTiles.map(t => t.title).join(', ');
        const availableTypes = incompatibleTiles[0]?.availableImages.join(', ') || 'no images';

        const message = `You cannot use "${newLayout}" layout because the following tiles don't have ${newLayout} images: ${tileNames}. 
These tiles have: ${availableTypes} images available.`;

        setLayoutAlertMessage(message);
        setShowLayoutAlert(true);

        // Show Swal alert
        Swal.fire({
          icon: 'warning',
          title: 'Layout Incompatible',
          html: `<div class="text-left">
          <p class="mb-2">You cannot use <strong>"${newLayout}"</strong> layout because:</p>
          <ul class="list-disc pl-5 mb-2">
            ${incompatibleTiles.map(t => `<li><strong>${t.title}</strong> doesn't have ${newLayout} images</li>`).join('')}
          </ul>
          <p class="mt-2">Available image types in these tiles: <strong>${availableTypes}</strong></p>
        </div>`,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Got it',
          timer: 5000,
          timerProgressBar: true
        });
      }
    }

    const firstCategory = layoutToCategoryOptions[newLayout]?.[0] || "";
    setFormData((prevData) => ({
      ...prevData,
      rowLayout: newLayout,
      tileCategory: firstCategory,
    }));
  };

  const DEFAULT_ROW_BG_URL =
    "https://images.do.cloudwalker.tv/images/row-assets/row_bg/1770697896228-PercyJacksonAndTheOlympians2_1920x900.webp";

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const updated = { ...prevData, [name]: value };
      if (name === "rowHeader" && !utmMediumManualEdited.current) {
        updated.utm_medium = value;
      }
      if (name === "utm_medium") {
        utmMediumManualEdited.current = true;
      }
      return updated;
    });
  };

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    await fetchTiles(value);
  };

  const tileCompatibilityAlert = (tile) => {
    if (!formData.rowLayout) return;
    const requiredField = (formData.tileCategory && categoryToImageField[formData.tileCategory])
      || layoutToImageField[formData.rowLayout];
    if (!requiredField) return;

    let hasImage = false;
    try {
      const raw = tile[requiredField];
      const parsed = Array.isArray(raw) ? raw : JSON.parse(raw || "[]");
      hasImage = Array.isArray(parsed) && parsed.length > 0;
    } catch {
      hasImage = false;
    }

    if (!hasImage) {
      const categoryLabel = formData.tileCategory || formData.rowLayout;
      Swal.fire({
        icon: "warning",
        title: "Missing Image",
        html: `<p><strong>${tile.title}</strong> doesn't have a <strong>${categoryLabel}</strong> image.</p>
               <p class="mt-2 text-sm text-gray-500">Upload a ${requiredField} image to this tile before using it in this row.</p>`,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
    }
  };

  const handleSelectTile = (tile) => {
    if (!selectedTiles.some((t) => t.id === tile.id)) {
      setSelectedTiles([...selectedTiles, tile]);
      setShowDropdown(true);
      tileCompatibilityAlert(tile);
    }
  };

  const fetchTiles = async (term = "") => {
    try {
      let query = supabase
        .from("tiles")
        .select("*")
        .limit(25)
        .order("created_at", { ascending: false });

      if (term.trim() !== "") {
        query = query.ilike("title", `%${term}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      setTiles(data || []);

      // 🔥 NEW: extract unique ids
      const languageIds = new Set();
      const sourceIds = new Set();

      (data || []).forEach((tile) => {
        // languages
        try {
          const langs =
            typeof tile.languages === "string"
              ? JSON.parse(tile.languages)
              : tile.languages || [];

          langs.forEach((id) => languageIds.add(id));
        } catch { }

        // source
        if (Array.isArray(tile.source)) {
          tile.source.forEach((id) => sourceIds.add(id));
        }
      });

      // 🔥 fetch only those ids
      if (languageIds.size > 0) {
        const { data: langData } = await supabase
          .from("languages")
          .select("id, languages")
          .in("id", [...languageIds]);

        const map = {};
        langData?.forEach((l) => (map[l.id] = l.languages));
        setLanguageMap(map);
      }

      if (sourceIds.size > 0) {
        const { data: srcData } = await supabase
          .from("source")
          .select("id, source")
          .in("id", [...sourceIds]);

        const map = {};
        srcData?.forEach((s) => (map[s.id] = s.source));
        // setSourceMap(map);
      }

    } catch (error) {
      console.error("Error fetching tile data:", error.message);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchTiles(""); // Fetch initial 50 recent tiles for dropdown
  }, []);

  useEffect(() => {
    const missingIds = new Set();
    selectedTiles.forEach((tile) => {
      const sources = Array.isArray(tile.source) ? tile.source : [];
      sources.forEach((id) => {
        // Skip non-uuid values (source stored as a plain name) — querying
        // the uuid "id" column with one of these errors the whole .in()
        // batch, silently blanking sourceMap resolution for every other
        // (legitimately uuid) id fetched alongside it.
        if (id && UUID_RE.test(id) && !sourceMap[id]) missingIds.add(id);
      });
    });
    if (missingIds.size > 0) {
      supabase
        .from("source")
        .select("id, source")
        .in("id", [...missingIds])
        .then(({ data }) => {
          if (data) {
            setSourceMap((prev) => {
              const updated = { ...prev };
              data.forEach((s) => (updated[s.id] = s.source));
              return updated;
            });
          }
        });
    }
  }, [selectedTiles]);

  // Init per-tile UTM params for newly added tiles using row-level defaults
  useEffect(() => {
    setTileUtmParams((prev) => {
      const updated = { ...prev };
      selectedTiles.forEach((tile) => {
        if (!updated[tile.tid]) {
          updated[tile.tid] = {
            utm_source: formData.utm_source || "cloudtvos",
            utm_medium: formData.utm_medium || "",
            utm_campaign: formData.utm_campaign || "",
          };
        }
      });
      // Remove deselected tiles
      Object.keys(updated).forEach((tid) => {
        if (!selectedTiles.some((t) => t.tid === tid)) delete updated[tid];
      });
      return updated;
    });
  }, [selectedTiles]);

  // When row-level UTM changes, apply to all existing tiles
  useEffect(() => {
  setTileUtmParams((prev) => {
    if (Object.keys(prev).length === 0) return prev;

    const updated = { ...prev };

    Object.keys(updated).forEach((tid) => {
      updated[tid] = {
        ...updated[tid], // keep saved tile values
        utm_source: updated[tid]?.utm_source || formData.utm_source || "cloudtvos",
        utm_medium: updated[tid]?.utm_medium || formData.utm_medium || "",
        utm_campaign: updated[tid]?.utm_campaign || formData.utm_campaign || "",
      };
    });

    return updated;
  });
}, [formData.utm_source, formData.utm_medium, formData.utm_campaign]);

  useEffect(() => {
    const category = formData.tileCategory || layoutToCategoryOptions[formData.rowLayout]?.[0];
    if (category) {
      setSuitablePage(0);
      fetchSuitableTiles(category, 0);
    } else {
      setSuitableTiles([]);
      setSuitableTotalCount(0);
    }
  }, [formData.rowLayout, formData.tileCategory]);

  useEffect(() => {
    const fetchRowForEdit = async () => {
      try {
        let rowDataToEdit = null;

        if (id) {
          const { data, error } = await supabase
            .from("row_data")
            .select("*")
            .eq("id", id)
            .single();

          if (error) throw error;
          rowDataToEdit = data;
        } else if (editRowProp) {
          rowDataToEdit = editRowProp;
        }

        if (rowDataToEdit) {
          const savedRowHeader = rowDataToEdit.row_header || "";
          const savedUtmMedium = rowDataToEdit.utm_medium || "";
          utmMediumManualEdited.current = !!(savedUtmMedium && savedUtmMedium !== savedRowHeader);

          const savedLayout = rowDataToEdit.row_layout || "";
          setFormData({
            rowLayout: savedLayout,
            tileCategory: layoutToCategoryOptions[savedLayout]?.[0] || "",
            rowHeader: savedRowHeader,
            row_width: rowDataToEdit.row_width || "",
            row_height: rowDataToEdit.row_height || "",
            row_bg: rowDataToEdit.row_bg || "",
            row_icon: rowDataToEdit.row_icon || "",
            rowType: rowDataToEdit.row_type || "",
            utm_source: rowDataToEdit.utm_source || "cloudtvos",
            utm_medium: savedUtmMedium || savedRowHeader,
            utm_campaign: rowDataToEdit.utm_campaign || "",
          });

          // Restore per-tile UTM params from saved row_items
          if (Array.isArray(rowDataToEdit.row_items)) {
            const savedTileUtm = {};
            rowDataToEdit.row_items.forEach((item) => {
              if (item.utm_source || item.utm_medium || item.utm_campaign) {
                savedTileUtm[item.tid] = {
                  utm_source: item.utm_source || rowDataToEdit.utm_source || "cloudtvos",
                  utm_medium: item.utm_medium || rowDataToEdit.utm_medium || "",
                  utm_campaign: item.utm_campaign || rowDataToEdit.utm_campaign || "",
                };
              }
            });
            if (Object.keys(savedTileUtm).length > 0) setTileUtmParams(savedTileUtm);
          }

          // Restore per-tile image overrides from saved row_items
          if (Array.isArray(rowDataToEdit.row_items)) {
            const savedImageOverrides = {};
            rowDataToEdit.row_items.forEach((item) => {
              if (item.image) savedImageOverrides[item.tid] = item.image;
            });
            if (Object.keys(savedImageOverrides).length > 0) setTileImageOverrides(savedImageOverrides);
          }

          if (
            savedLayout !== "highlight" &&
            Array.isArray(rowDataToEdit.row_items) &&
            rowDataToEdit.row_items.length > 0
          ) {
            const { data: tilesData, error: tilesError } = await fetchInChunks(
              "tiles",
              "*",
              "tid",
              rowDataToEdit.row_items.map((r) => r.tid)
            );

            if (tilesError) throw tilesError;
            // setSelectedTiles(tilesData);

            const orderedTiles = rowDataToEdit.row_items
              .sort((a, b) => a.order - b.order)
              .map((item) => tilesData.find((tile) => tile.tid === item.tid))
              .filter(Boolean);

            setSelectedTiles(orderedTiles);

          }

          if (
            savedLayout === "highlight" &&
            Array.isArray(rowDataToEdit.row_items) &&
            rowDataToEdit.row_items.length > 0
          ) {
            const sortedItems = [...rowDataToEdit.row_items].sort((a, b) => a.order - b.order);

            const uniqueBaseTids = [...new Set(sortedItems.map((it) => it.baseTid).filter(Boolean))];
            const uniqueOverlayTids = [...new Set(sortedItems.map((it) => it.overlayTid).filter(Boolean))];

            const { data: baseTilesData, error: baseTilesError } = uniqueBaseTids.length
              ? await fetchInChunks("tiles", "*", "tid", uniqueBaseTids)
              : { data: [], error: null };
            if (baseTilesError) throw baseTilesError;

            const { data: overlayTilesData, error: overlayTilesError } = uniqueOverlayTids.length
              ? await fetchInChunks("tiles", "*", "tid", uniqueOverlayTids)
              : { data: [], error: null };
            if (overlayTilesError) throw overlayTilesError;

            setBaseTiles(baseTilesData || []);

            // Each saved row only ever represents ONE dimension (item.baseField)
            // — the other two dimensions' base images are re-derived here from
            // the freshly-fetched base tile (same poster/portrait/background
            // arrays HighlightRowBuilder's pooling effect pools from), paired
            // by this item's position within its own baseTid's sequence, so
            // the slot's `key` matches exactly what that pooling effect would
            // independently compute for the same (tile, index) pair. The
            // dimension that WAS saved shows its real composite immediately;
            // the other two show the plain base image (not yet composited)
            // until the admin touches something, which regenerates all three.
            const baseTidCounters = {};
            setHighlightSlots(
              sortedItems.map((item) => {
                const overlayTile = (overlayTilesData || []).find((t) => t.tid === item.overlayTid) || null;
                const tile = (baseTilesData || []).find((t) => t.tid === item.baseTid) || null;

                // Prefer the saved exact index; only guess via a running
                // per-tile counter for legacy rows saved before tileIndex
                // was recorded (safe only when a dimension has no gaps).
                const fallbackIndex = baseTidCounters[item.baseTid] ?? 0;
                baseTidCounters[item.baseTid] = fallbackIndex + 1;
                const i = item.tileIndex ?? fallbackIndex;

                const posterArr = tile ? safeJsonParseArray(tile.poster) : [];
                const portraitArr = tile ? safeJsonParseArray(tile.portrait) : [];
                const backgroundArr = tile ? safeJsonParseArray(tile.background) : [];

                const dimension = item.baseField || "poster";
                const baseImageByDim = {
                  poster: dimension === "poster" ? item.baseImage : posterArr[i] || null,
                  portrait: dimension === "portrait" ? item.baseImage : portraitArr[i] || null,
                  background: dimension === "background" ? item.baseImage : backgroundArr[i] || null,
                };

                const slotKey = buildSlotKey(
                  item.baseTid,
                  ["poster", "portrait", "background"].map((d) => baseImageByDim[d] || "_").join("|")
                );

                const slot = {
                  key: slotKey,
                  // item.order is unique per row, so this stays unique even
                  // when the same base image trio legitimately appears twice.
                  id: `${slotKey}::${item.order}`,
                  baseTid: item.baseTid,
                  tileIndex: i,
                  overlayTid: item.overlayTid || null,
                  overlayTitle: overlayTile?.title || "",
                  overlayTileData: overlayTile,
                  alignment: item.alignment || "center",
                  requestId: 1,
                };

                ["poster", "portrait", "background"].forEach((d) => {
                  const cap = d[0].toUpperCase() + d.slice(1);
                  const baseImg = baseImageByDim[d];
                  slot[`baseImage${cap}`] = baseImg;
                  slot[`${d}OverlayXPct`] = null;
                  slot[`${d}OverlayYPct`] = null;
                  slot[`${d}OverlayWidthPct`] = null;
                  slot[`${d}OverlayHeightPct`] = null;
                  slot[`${d}HasCutout`] = null;
                  slot[`${d}Blob`] = null;
                  slot[`${d}Generating`] = false;
                  // Badge text/styling is independent per dimension — only
                  // the dimension that WAS saved (this row) carries its real
                  // badge config back in; the other two start blank, same as
                  // a brand-new slot, since this row never stored their badges.
                  LABEL_FIELD_KEYS.forEach((k) => {
                    slot[`${d}${k[0].toUpperCase()}${k.slice(1)}`] =
                      d === dimension && item[k] !== undefined && item[k] !== null
                        ? item[k]
                        : DEFAULT_LABEL_VALUES[k];
                  });
                  if (d === dimension) {
                    slot[`${d}PreviewUrl`] = item.image ? resolveHighlightUrl(item.image, minioBaseUrl) : null;
                    slot[`saved${cap}`] = item.image || null;
                    slot[`${d}Width`] = item.tileWidth || null;
                    slot[`${d}Height`] = item.tileHeight || null;
                  } else {
                    slot[`${d}PreviewUrl`] = baseImg ? resolveHighlightUrl(baseImg, minioBaseUrl) : null;
                    slot[`saved${cap}`] = null;
                    slot[`${d}Width`] = null;
                    slot[`${d}Height`] = null;
                  }
                });

                return slot;
              })
            );
          }

          setEditRow(rowDataToEdit);
        }
      } catch (err) {
        console.error("Error fetching row for edit:", err);
      } finally {
        setIsFormLoading(false);
      }
    };

    fetchRowForEdit();
  }, [id, editRowProp]);

  const toggleTileSelection = (tile) => {
    const isAlreadySelected = selectedTiles.some((t) => t.id === tile.id);
    const newSelectedTiles = isAlreadySelected
      ? selectedTiles.filter((t) => t.id !== tile.id)
      : [...selectedTiles, tile];

    setSelectedTiles(newSelectedTiles);

    if (!isAlreadySelected) {
      tileCompatibilityAlert(tile);
    }
  };

  // const handleDragStart = (e, index) => {
  //   e.dataTransfer.setData("draggedIndex", index);
  // };

  // const handleDragOver = (e) => {
  //   e.preventDefault();
  // };

  const draggedIndexRef = useRef(null);

  const handleDragStart = (e, index) => {
    draggedIndexRef.current = index;
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();

    const draggedIndex = draggedIndexRef.current;
    if (draggedIndex === index) return;

    const updated = [...selectedTiles];
    const draggedItem = updated[draggedIndex];

    // remove dragged
    updated.splice(draggedIndex, 1);

    // insert at new position
    updated.splice(index, 0, draggedItem);

    draggedIndexRef.current = index;
    setSelectedTiles(updated);
  };


  const handleDrop = (e, index) => {
    const draggedIndex = e.dataTransfer.getData("draggedIndex");
    const draggedItem = selectedTiles[draggedIndex];

    const newSelectedTiles = [...selectedTiles];
    newSelectedTiles.splice(draggedIndex, 1);
    newSelectedTiles.splice(index, 0, draggedItem);

    setSelectedTiles(newSelectedTiles);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files[0],
    }));
  };

  const replaceImageWithMinIO = async (file, folder, currentPath) => {
    const BUCKET_NAME = "images";
    const basePath = `row-assets/${folder}`;

    if (currentPath && !currentPath.includes('row-assets')) {
      try {
        await minioClient.send(
          new DeleteObjectCommand({
            Bucket: BUCKET_NAME,
            Key: currentPath,
          })
        );
      } catch (error) {
        console.error("Failed to delete old MinIO image:", error.message);
      }
    }

    if (file instanceof File) {
      const fileName = `${basePath}/${Date.now()}-${file.name}`;
      const buffer = await file.arrayBuffer();
      const command = new PutObjectCommand({
        Bucket: BUCKET_NAME,
        Key: fileName,
        Body: buffer,
        ContentType: file.type,
      });

      try {
        await minioClient.send(command);
        return fileName;
      } catch (error) {
        console.error("Error uploading image to MinIO:", error.message);
        throw new Error("Upload to MinIO failed");
      }
    }

    return currentPath || "";
  };

  // Highlight composite images are stored alongside the base tile's own
  // images (tiles/<baseTid>/...), matching how every other tile image is
  // organized in the bucket — not under a separate row-assets/ prefix.
  const uploadHighlightComposite = async (file, baseTid) => {
    const BUCKET_NAME = "images";
    const fileName = `tiles/${baseTid}/${file.name}`;
    const buffer = await file.arrayBuffer();
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: fileName,
      Body: buffer,
      ContentType: file.type,
    });

    try {
      await minioClient.send(command);
      return fileName;
    } catch (error) {
      console.error("Error uploading highlight composite to MinIO:", error.message);
      throw new Error("Upload to MinIO failed");
    }
  };

  const handleRowDataChange = async (e) => {
    e.preventDefault();

    // Check if there are compatibility issues and ask for confirmation
    let shouldContinue = true;

    if (formData.rowLayout && selectedTiles.length > 0) {
      const incompatibleTiles = checkLayoutCompatibility(selectedTiles, formData.rowLayout, formData.tileCategory);
      if (incompatibleTiles.length > 0) {
        const tileNames = incompatibleTiles.map(t => t.title).join(', ');
        const availableTypes = incompatibleTiles[0]?.availableImages.join(', ') || 'no images';

        const result = await Swal.fire({
          icon: 'warning',
          title: 'Layout Compatibility Warning',
          html: `<div class="text-left">
            <p class="mb-2">You are using <strong>"${formData.rowLayout}"</strong> layout but these tiles don't have ${formData.rowLayout} images:</p>
            <ul class="list-disc pl-5 mb-3">
              ${incompatibleTiles.map(t => `<li><strong>${t.title}</strong></li>`).join('')}
            </ul>
            <p>Available image types: <strong>${availableTypes}</strong></p>
            <p class="mt-3 text-sm text-gray-600">Do you want to continue anyway?</p>
          </div>`,
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, continue',
          cancelButtonText: 'No, go back'
        });

        shouldContinue = result.isConfirmed;
      }
    }

    if (!shouldContinue) {
      return;
    }

    // 🔒 VALIDATION: Row Header must not be empty or spaces
    if (!formData.rowHeader || formData.rowHeader.trim().length === 0) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Row Header cannot be empty",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-lg rounded-md p-3",
        },
      });
      return;
    }


    if (formData.rowLayout !== "highlight" && (!selectedTiles || selectedTiles.length === 0)) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "You must atleast select 1 tile to continue",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-lg rounded-md p-3",
        },
      });
      return; // ⛔ stop save/update
    }

    if (formData.rowLayout === "highlight" && highlightSlots.length === 0) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Add at least one base tile with background images",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-lg rounded-md p-3",
        },
      });
      return; // ⛔ stop save/update
    }

    if (
      formData.rowLayout === "highlight" &&
      highlightSlots.some((s) => s.posterGenerating || s.portraitGenerating || s.backgroundGenerating)
    ) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Wait for images to finish generating before saving",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-lg rounded-md p-3",
        },
      });
      return; // ⛔ stop save/update
    }

    setIsSubmitting(true);
    try {
      if (formData.rowLayout === "highlight") {
        // Highlight rows are saved as up to 3 separate row_data records — one
        // per dimension (Poster/Portrait/Background) — since a row's
        // exported rowLayout is a single value and each dimension needs its
        // own homogeneous set of items. Each slot in highlightSlots already
        // carries an independently-generated composite per dimension (see
        // HighlightRowBuilder's runGeneration). Upserted by row_header so
        // re-saving an edited row refreshes its siblings instead of piling
        // up duplicates.
        const iconPath = await replaceImageWithMinIO(
          formData.row_icon,
          "row_icon",
          editRow?.row_icon
        );
        const backgroundPath = formData.row_bg
          ? await replaceImageWithMinIO(formData.row_bg, "row_bg", editRow?.row_bg)
          : DEFAULT_ROW_BG_URL;

        const DIMENSION_LABELS = { poster: "Poster", portrait: "Portrait", background: "Background" };
        // When editing an existing dimension row, formData.rowHeader is
        // pre-filled with its full saved header — already including a
        // "(Poster/Portrait/Background)" suffix. Strip it before re-suffixing
        // per dimension below, so editing "Jungle beats (Background)" re-saves
        // as "Jungle beats (Poster/Portrait/Background)" and correctly
        // upserts onto its siblings, instead of stacking a second suffix.
        const baseRowHeader = formData.rowHeader.replace(
          /\s*\((?:Poster|Portrait|Background)\)\s*$/i,
          ""
        ).trim();
        let savedCount = 0;

        for (const dimension of ["poster", "portrait", "background"]) {
          const cap = dimension[0].toUpperCase() + dimension.slice(1);
          const relevantSlots = highlightSlots.filter((slot) => slot[`baseImage${cap}`]);
          if (relevantSlots.length === 0) continue;

          const rowItems = await Promise.all(
            relevantSlots.map(async (slot, i) => {
              const width = slot[`${dimension}Width`];
              const height = slot[`${dimension}Height`];
              const resolution = width && height ? `_${width}x${height}` : "";
              const blob = slot[`${dimension}Blob`];
              const imagePath = blob
                ? await uploadHighlightComposite(
                    new File(
                      [blob],
                      `highlight_${dimension}_${i}${resolution}_${Date.now()}.webp`,
                      { type: "image/webp" }
                    ),
                    slot.baseTid
                  )
                : slot[`saved${cap}`] || slot[`baseImage${cap}`];

              return {
                order: i + 1,
                image: imagePath,
                baseTid: slot.baseTid,
                baseImage: slot[`baseImage${cap}`],
                baseField: dimension,
                // This slot's position within its own base tile's pooled
                // sequence — lets edit-reload re-derive the OTHER two
                // dimensions' base images by exact index instead of guessing.
                tileIndex: slot.tileIndex,
                overlayTid: slot.overlayTid || null,
                overlayField: slot.overlayTid ? dimension : null,
                alignment: slot.overlayTid ? slot.alignment : null,
                tileWidth: width || null,
                tileHeight: height || null,
                // Badge text/styling is configured independently per
                // dimension (slot[`${dimension}LabelText`] etc.) — only THIS
                // dimension's own badge config is saved onto this row's item.
                labelText: slot[`${dimension}LabelText`] || null,
                labelXPct: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelXPct`] : null,
                labelYPct: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelYPct`] : null,
                labelFontFamily: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelFontFamily`] : null,
                labelFontSize: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelFontSize`] : null,
                labelFontWeight: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelFontWeight`] : null,
                labelFontStyle: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelFontStyle`] : null,
                labelShowBackground: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelShowBackground`] : null,
                labelTextColorType: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelTextColorType`] : null,
                labelTextColor: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelTextColor`] : null,
                labelTextGradientFrom: slot[`${dimension}LabelText`]
                  ? slot[`${dimension}LabelTextGradientFrom`]
                  : null,
                labelTextGradientTo: slot[`${dimension}LabelText`]
                  ? slot[`${dimension}LabelTextGradientTo`]
                  : null,
                labelBgColorType: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelBgColorType`] : null,
                labelBgColor: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelBgColor`] : null,
                labelBgGradientFrom: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelBgGradientFrom`] : null,
                labelBgGradientTo: slot[`${dimension}LabelText`] ? slot[`${dimension}LabelBgGradientTo`] : null,
              };
            })
          );

          const rowHeader = `${baseRowHeader} (${DIMENSION_LABELS[dimension]})`;
          const dimensionRowData = {
            row_height: formData.row_height,
            row_width: formData.row_width,
            row_header: rowHeader,
            row_layout: "highlight",
            row_items: rowItems,
            row_icon: iconPath,
            row_bg: backgroundPath,
            row_type: formData.rowType || "",
            utm_source: formData.utm_source || "cloudtvos",
            utm_medium: formData.utm_medium || "",
            utm_campaign: formData.utm_campaign || "",
            updated_by: getCurrentUser(),
            updated_at: new Date().toISOString(),
          };

          const { data: existingRows, error: existingError } = await supabase
            .from("row_data")
            .select("id")
            .eq("row_header", rowHeader)
            .eq("row_layout", "highlight")
            .limit(1);
          if (existingError) throw existingError;

          if (existingRows && existingRows.length > 0) {
            const { error } = await supabase
              .from("row_data")
              .update(dimensionRowData)
              .eq("id", existingRows[0].id);
            if (error) throw error;
          } else {
            const { error } = await supabase.from("row_data").insert([dimensionRowData]);
            if (error) throw error;
          }
          savedCount += 1;
        }

        if (savedCount === 0) {
          throw new Error(
            "None of the slots had a Poster, Portrait, or Background image — nothing was saved."
          );
        }

        Swal.fire({
          position: "top-end",
          icon: "success",
          title: `${savedCount} row${savedCount > 1 ? "s" : ""} saved`,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: { popup: "shadow-lg rounded-md p-3" },
        });

        navigate("/row");
        return;
      }

      const iconPath = await replaceImageWithMinIO(
        formData.row_icon,
        "row_icon",
        editRow?.row_icon
      );


      const backgroundPath = formData.row_bg
        ? await replaceImageWithMinIO(formData.row_bg, "row_bg", editRow?.row_bg)
        : DEFAULT_ROW_BG_URL;

      const rowData = {
        row_height: formData.row_height,
        row_width: formData.row_width,
        row_header: formData.rowHeader,
        row_layout: formData.rowLayout,
        row_items: selectedTiles.map((item, i) => {
          const base = { tid: item.tid, order: i + 1 };

          if (tileImageOverrides[item.tid]) {
            base.image = tileImageOverrides[item.tid];
          }

          const isEligible = isUtmEligibleSource(item.source);

          if (isEligible) {
            const sourceNames = (Array.isArray(item.source) ? item.source : [])
              .map((id) => resolveSourceName(id).toLowerCase());
            const isItemSunNxt = sourceNames.some((n) => n.replace(/\s+/g, "").includes("sunnxt"));

            const raw = item.target;
            let baseUrl = null;
            try {
              const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
              baseUrl = Array.isArray(parsed) ? (parsed[0] || null) : (String(parsed) || null);
            } catch {
              baseUrl = typeof raw === "string" ? raw : null;
            }

            if (isItemSunNxt) {
              // SunNXT only ever carries a single "source" param, always
              // appended (defaulting to "cloudTV" when not customized) —
              // not the generic utm_source/medium/campaign scheme below.
              const sourceParam = tileUtmParams[item.tid]?.source || "cloudTV";
              if (baseUrl) {
                const separator = baseUrl.includes("?") ? "&" : "?";
                base.target = `${baseUrl}${separator}source=${encodeURIComponent(sourceParam)}`;
              }
              base.source_param = sourceParam;

              return base;
            }

            const tileUtm = tileUtmParams[item.tid] || {
              utm_source: formData.utm_source || "cloudtvos",
              utm_medium: formData.utm_medium || "",
              utm_campaign: formData.utm_campaign || "",
            };

            const utmParts = [];
            if (tileUtm.utm_source) utmParts.push(`utm_source=${encodeURIComponent(tileUtm.utm_source)}`);
            if (tileUtm.utm_medium) utmParts.push(`utm_medium=${encodeURIComponent(tileUtm.utm_medium)}`);
            if (tileUtm.utm_campaign) utmParts.push(`utm_campaign=${encodeURIComponent(tileUtm.utm_campaign)}`);
            const utmString = utmParts.join("&");

            if (baseUrl && utmString) {
              const separator = baseUrl.includes("?") ? "&" : "?";
              base.target = `${baseUrl}${separator}${utmString}`;
            }
            base.utm_source = tileUtm.utm_source;
            base.utm_medium = tileUtm.utm_medium;
            base.utm_campaign = tileUtm.utm_campaign;
          }

          return base;
        }),
        row_icon: iconPath,
        row_bg: backgroundPath,
        row_type: formData.rowType || "",
        utm_source: formData.utm_source || "cloudtvos",
        utm_medium: formData.utm_medium || "",
        utm_campaign: formData.utm_campaign || "",
        updated_by: getCurrentUser(),
        updated_at: new Date().toISOString(),
      };

      if (editRow) {
        const { error } = await supabase
          .from("row_data")
          .update(rowData)
          .eq("id", editRow.id);
        if (error) throw error;

        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Row updated successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-lg rounded-md p-3",
          },
        });
      } else {
        const { error } = await supabase.from("row_data").insert([rowData]);
        if (error) throw error;

        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Row inserted successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-lg rounded-md p-3",
          },
        });
      }

      const rowId = editRow?.id || id;
      if (rowId) {
        navigate(`/row/${rowId}`, { replace: true });
      } else {
        navigate("/row");
      }
    } catch (error) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `Error: ${error.message}`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-lg rounded-md p-3",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFormLoading || isSubmitting) {
    return (
      <div className="h-screen overflow-y-auto overflow-x-hidden p-4 bg-gray-50">
        <RowFormSkeleton />
      </div>
    );
  }

  return (
    <div ref={pageContainerRef} className="h-screen overflow-y-auto overflow-x-hidden p-4 bg-gray-50">
      <div className="mb-4">
        <Button
          variant="secondary"
          className="flex items-center gap-2 px-4 py-2"
          onClick={() => navigate(-1)}
        >
          <span className="text-lg">←</span>
          Back
        </Button>
      </div>
      <form
        onSubmit={handleRowDataChange}
        className="space-y-4 border-slate-300 shadow-md border bg-gray-100 p-4 mx-auto rounded-md relative max-w-6xl w-full"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Row Layout
          </label>
          <select
            required
            value={formData.rowLayout}
            onChange={handleInputChange}
            className={`mt-1 ${!formData.rowLayout && "text-gray-400"
              } outline-none block w-full py-2 px-3 border rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
            name="rowLayout"
          >
            {[
              "",
              "landscape",
              "portrait",
              "longPortrait",
              "square",
              "background",
              "featureBanner",
              "heroBanner",
              "titleLogo",
              "banner",
              "highlight",
              // "landscapePoster",
              // "mainBanner",
            ].map((rowLayout, i) => (
              <option
                key={i}
                value={rowLayout}
                hidden={!rowLayout}
                className="text-black"
              >
                {!rowLayout
                  ? "Select Row Layout"
                  : {
                      landscape: "Landscape",
                      portrait: "Portrait",
                      longPortrait: "Long Portrait",
                      square: "Square",
                      background: "Background",
                      featureBanner: "Feature Banner",
                      heroBanner: "Hero Banner",
                      titleLogo: "Title Logo",
                      banner: "Main Banner (Masthead)",
                      highlight: "Highlight",
                      // landscapePoster: "Landscape Poster",
                      // mainBanner: "Main Banner",
                    }[rowLayout] ?? rowLayout.charAt(0).toUpperCase() + rowLayout.slice(1)}
              </option>
            ))}
          </select>
        </div>
        {formData.rowLayout && formData.rowLayout !== "highlight" && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tile Category
            </label>
            <select
              value={formData.tileCategory}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, tileCategory: e.target.value }))
              }
              className="mt-1 block w-full py-2 px-3 border rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              name="tileCategory"
            >
              {(layoutToCategoryOptions[formData.rowLayout] || []).map((cat, i) => (
                <option key={i} value={cat}>
                  {{
                    poster: "Poster",
                    mainBanner: "Main Banner",
                    featureBanner: "Feature Banner",
                    heroBanner: "Hero Banner",
                    landscape: "Landscape",
                    portrait: "Portrait",
                    longPortrait: "Long Portrait",
                    "long portrait": "Long Portrait",
                    square: "Square",
                    background: "Background",
                    titleLogo: "Title Logo",
                    banner: "Banner",
                    // landscapePoster: "Landscape Poster",
                  }[cat] ?? cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Row Header
          </label>
          <input
            type="text"
            name="rowHeader"
            value={formData.rowHeader}
            onChange={handleInputChange}
            className="mt-1 block w-full py-2 px-4 border rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Enter Row Header"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Row Width
          </label>
          <input
            type="text"
            name="row_width"
            value={formData.row_width}
            className="mt-1 block w-full py-2 px-4 border rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Enter tile width"
            onChange={handleInputChange}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Row Height
          </label>
          <input
            value={formData.row_height}
            type="text"
            name="row_height"
            className="mt-1 block w-full py-2 px-4 border rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Enter tile height"
            onChange={handleInputChange}
          />
        </div>
        {/* Row Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Row Type
          </label>
          <select
            name="rowType"
            value={formData.rowType}
            onChange={handleInputChange}
            className={`mt-1 block w-full py-2 px-3 border rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!formData.rowType && "text-gray-400"
              }`}
          >
            <option value="">Normal Row</option>
            <option value="carousel">Carousel</option>
            <option value="immersive">Immersive</option>
          </select>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 col-span-1 sm:col-span-2 p-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">
              Icon
            </label>
            <input
              type="file"
              name="row_icon"
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-indigo-300 file:text-black hover:file:bg-indigo-400"
              onChange={handleFileChange}
            />
          </div>
          {formData.row_icon && (
            <div className="w-full sm:w-3/4 md:w-1/2 flex justify-center items-center h-40">
              <img
                src={
                  formData.row_icon instanceof File
                    ? URL.createObjectURL(formData.row_icon)
                    : `${minioBaseUrl}${formData.row_icon}`
                }
                alt="Icon Preview"
                className="rounded-md h-full max-h-full object-cover"
              />
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-center col-span-1 sm:col-span-2 gap-4 p-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">
              Background
            </label>
            <input
              type="file"
              name="row_bg"
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-indigo-300 file:text-black hover:file:bg-indigo-400"
              onChange={handleFileChange}
            />
          </div>
          {formData.row_bg && (
            <div className="w-full sm:w-1/2 flex justify-center items-center h-40">
              <img
                src={
                  formData.row_bg instanceof File
                    ? URL.createObjectURL(formData.row_bg)
                    : `${minioBaseUrl}${formData.row_bg}`
                }
                alt="Background Preview"
                className="rounded-md h-full max-h-full object-fill"
              />
            </div>
          )}
        </div>

        {formData.rowLayout === "highlight" ? (
          <HighlightRowBuilder
            baseTiles={baseTiles}
            setBaseTiles={setBaseTiles}
            slots={highlightSlots}
            setSlots={setHighlightSlots}
            minioBaseUrl={minioBaseUrl}
            titleLogoBaseUrl={titleLogoBaseUrl}
            baseImageField={baseImageField}
            setBaseImageField={setBaseImageField}
          />
        ) : (
          <>
        {/* Suitable Tiles Section */}
        {formData.rowLayout && (
          <div className="relative border border-gray-300 rounded-md p-4 bg-white">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">
              Suitable Tiles for{" "}
              <span className="text-blue-600 capitalize">{formData.tileCategory || formData.rowLayout}</span>{" "}
              category
              {!suitableLoading && suitableTotalCount > 0 && (
                <span className="ml-2 text-gray-400 font-normal">
                  ({suitableTotalCount} found)
                </span>
              )}
            </h2>

            {suitableTiles.length === 0 && !suitableLoading ? (
              <p className="text-sm text-gray-400 py-2">
                No tiles found with {formData.tileCategory || formData.rowLayout} images.
              </p>
            ) : (
              <>
                <div className="relative">
                  {/* Inline loader overlay — only covers this div */}
                  {suitableLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-70 rounded">
                      <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex gap-4 overflow-x-auto pb-3" style={{ scrollbarWidth: "thin" }}>
                    {suitableTiles.map((tile) => {
                      const resolveFirstImage = (field) => {
                        try {
                          const raw = tile[field];
                          if (!raw) return null;
                          const base = field === "titleLogo" ? titleLogoBaseUrl : minioBaseUrl;
                          const buildUrl = (src) => {
                            if (!src) return null;
                            if (src.startsWith("http")) return src;
                            const path = src.startsWith("tiles/") ? src : `tiles/${src}`;
                            return `${base}${path}`;
                          };
                          if (typeof raw === "string" && !raw.trim().startsWith("[")) {
                            return buildUrl(raw.trim());
                          }
                          const arr = Array.isArray(raw) ? raw : JSON.parse(raw);
                          return buildUrl(Array.isArray(arr) ? arr[0] : null);
                        } catch {
                          return null;
                        }
                      };
                      // For portrait-type fields: prefer longPortrait, fall back to portrait
                      const defaultImageUrl = (suitableImageField === "portrait" || suitableImageField === "longPortrait")
                        ? (resolveFirstImage("longPortrait") || resolveFirstImage("portrait"))
                        : resolveFirstImage(suitableImageField);
                      const isAdded = selectedTiles.some((t) => t.id === tile.id);
                      const imageOptions = getTileImageOptions(tile, suitableImageField);
                      const chosenImage = tileImageOverrides[tile.tid] || null;
                      const imageUrl = chosenImage
                        ? resolveTileImageUrl(chosenImage, suitableImageField)
                        : defaultImageUrl;

                      const thumbSize = {
                        poster:        { width: "172px", height: "97px"  },
                        landscape:     { width: "172px", height: "97px"  },
                        portrait:      { width: "108px", height: "144px" },
                        longPortrait:  { width: "86px",  height: "173px" },
                        square:        { width: "130px", height: "130px" },
                        banner:        { width: "230px", height: "58px"  },
                        featureBanner: { width: "230px", height: "43px"  },
                        heroBanner:    { width: "180px", height: "90px"  },
                        titleLogo:     { width: "180px", height: "72px"  },
                        background:    { width: "172px", height: "97px"  },
                        // landscapePoster:{ width: "172px", height: "97px"  },
                        mainBanner:    { width: "230px", height: "58px"  },
                      }[suitableImageField] || { width: "172px", height: "97px" };

                      return (
                        <div
                          key={tile.tid || tile.id}
                          className="relative flex-shrink-0 flex flex-col items-center gap-1"
                          style={{ width: thumbSize.width }}
                        >
                          {/* Image */}
                          <div className="relative rounded overflow-hidden bg-gray-100 border border-gray-200" style={thumbSize}>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={tile.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                                No img
                              </div>
                            )}
                            {isAdded && (
                              <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                                <FaCheckCircle className="text-white" size={13} />
                              </div>
                            )}
                          </div>
                          {/* Title */}
                          <p className="text-sm text-gray-700 text-center leading-tight truncate w-full" title={tile.title}>
                            {tile.title}
                          </p>
                          {/* Action buttons — always visible */}
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewTile(tile);
                                setShowPreviewModal(true);
                              }}
                              className="p-1.5 rounded text-blue-500 hover:text-blue-700 border border-blue-200 hover:bg-blue-50"
                              title="Preview"
                            >
                              <FaEye size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleTileSelection(tile)}
                              className={`px-3 py-1 rounded text-sm font-bold border ${isAdded
                                ? "bg-red-100 text-red-600 border-red-300 hover:bg-red-200"
                                : "bg-green-100 text-green-700 border-green-300 hover:bg-green-200"
                                }`}
                              title={isAdded ? "Remove" : "Add"}
                            >
                              {isAdded ? "−" : "+"}
                            </button>
                          </div>

                          {/* Image swiper — every uploaded image for this field
                              shows below the tile by default; clicking one
                              pins it and updates the tile's image above. */}
                          {imageOptions.length > 1 && (
                            <div
                              className="mt-1.5 w-full flex items-center justify-center gap-1.5 overflow-x-auto py-1"
                              style={{ scrollbarWidth: "none" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {imageOptions.map((path, i) => {
                                const isChosen = chosenImage === path;
                                return (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                      setTileImageOverrides((prev) => ({ ...prev, [tile.tid]: path }));
                                    }}
                                    title="Set as tile image"
                                    className={`relative shrink-0 rounded-md overflow-hidden border-2 bg-gray-100 transition-all duration-150 ${isChosen ? "border-blue-500 ring-2 ring-blue-200 scale-110 z-10" : "border-gray-200 opacity-70 hover:opacity-100 hover:border-blue-300"}`}
                                    style={{ width: "34px", height: "34px" }}
                                  >
                                    <img
                                      src={resolveTileImageUrl(path, suitableImageField)}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                    {isChosen && (
                                      <span className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-0.5">
                                        <FaCheckCircle className="text-white" size={8} />
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>{/* end relative wrapper */}

                {/* Pagination controls */}
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-200">
                  <button
                    type="button"
                    disabled={suitablePage === 0 || suitableLoading}
                    onClick={() => {
                      const newPage = suitablePage - 1;
                      setSuitablePage(newPage);
                      fetchSuitableTiles(formData.tileCategory || formData.rowLayout, newPage);
                    }}
                    className="px-3 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  <span className="text-xs text-gray-500">
                    {suitablePage * SUITABLE_PAGE_SIZE + 1}–{Math.min((suitablePage + 1) * SUITABLE_PAGE_SIZE, suitableTotalCount)} of {suitableTotalCount} tiles
                  </span>
                  <button
                    type="button"
                    disabled={(suitablePage + 1) * SUITABLE_PAGE_SIZE >= suitableTotalCount || suitableLoading}
                    onClick={() => {
                      const newPage = suitablePage + 1;
                      setSuitablePage(newPage);
                      fetchSuitableTiles(formData.tileCategory || formData.rowLayout, newPage);
                    }}
                    className="px-3 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700">
            Search Tile
          </label>
          <SearchBar
            placeholder="Search Created Tiles"
            value={searchTerm}
            onChange={handleSearchChange}
            inputClassName="py-1 bg-white mt-1"
          />
          {showDropdown && searchTerm && (
            <ul className="absolute bottom-full mb-1 z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-[70vh] overflow-auto">
              {Tiles.length > 0 ? (
                Tiles.map((tile, i) => (

                  <li
                    key={tile.tid || tile.id || i}
                    onClick={() => handleSelectTile(tile)}
                    className={`px-4 flex items-center gap-4 py-2 text-sm text-gray-700 cursor-pointer mb-0.5 ${selectedTiles.some((t) => t.id === tile.id)
                        ? "bg-gray-100"
                        : "bg-white hover:bg-gray-100"
                      }`}
                  >
                    {/* IMAGE */}
                    <div className="w-[100px]">
                      <img
                        src={(() => {
                          try {
                            const parsed = JSON.parse(tile.poster || "[]");
                            return parsed?.[0]
                              ? parsed[0].startsWith("http")
                                ? parsed[0]
                                : `${minioBaseUrl}${parsed[0]}`
                              : "https://via.placeholder.com/60x40?text=No";
                          } catch {
                            return "https://via.placeholder.com/60x40?text=No";
                          }
                        })()}
                        alt="poster"
                        className="w-[100vh] h-[50px] object-cover rounded"
                      />
                    </div>

                    {/* TITLE */}
                    <div
                      className="w-[220px] truncate"
                      onClick={() => handleSelectTile(tile)}
                    >
                      {tile.title}
                    </div>

                    {/* SOURCE */}
                    <div className="w-[180px] truncate">
                      {(() => {
                        try {
                          if (!Array.isArray(tile.source)) return "N/A";

                          const mapped = tile.source
                            .map((id) => resolveSourceName(id))
                            .filter(Boolean);

                          return mapped.length ? mapped.join(", ") : "N/A";
                        } catch {
                          return "N/A";
                        }
                      })()}
                    </div>

                    {/* LANGUAGE */}
                    <div className="w-[180px] truncate">
                      {(() => {
                        try {
                          const raw = tile.languages;
                          const arr =
                            typeof raw === "string"
                              ? JSON.parse(raw)
                              : Array.isArray(raw)
                                ? raw
                                : [];

                          const mapped = arr
                            .map((id) => languageMap[id])
                            .filter(Boolean);

                          return mapped.length ? mapped.join(", ") : "N/A";
                        } catch {
                          return "N/A";
                        }
                      })()}
                    </div>

                    {/* ACTIONS */}
                    <div className="flex items-center gap-3 ml-auto">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPreviewTile(tile);
                          setShowPreviewModal(true);
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <FaEye />
                      </button>

                      {selectedTiles.some((t) => t.id === tile.id) && (
                        <p className="text-xs flex items-center gap-1">
                          Added
                          <FaCheckCircle className="text-green-600" />
                        </p>
                      )}
                    </div>
                  </li>
                ))
              ) : (
                <li className="px-4 py-2 text-sm text-gray-500">
                  No tiles found.
                </li>

              )}
            </ul>
          )}

          {selectedTiles.length > 0 && formData.rowLayout && (
            <div className="mt-2 text-xs text-gray-500">
              <span>Available images in selected tiles: </span>
              {/* NEW: Show all available image types across selected tiles */}
              {[...new Set(selectedTiles.flatMap(t => getAvailableImageTypes(t)))].join(', ')}
            </div>
          )}
        </div>
        <div className="text-end">
          <Button
            variant="primary"
            className="px-5 text-sm"
            onClick={() => setShowTilesModal(true)}
          >
            Show All Tiles
          </Button>
        </div>

        {selectedTiles &&
          selectedTiles.length > 0 &&
          selectedTiles.map((t, index) => {
            const isEligible = isUtmEligibleSource(t.source);
            const isActive = expandedUtmTile === t.tid;
            const imageOptions = getTileImageOptions(t, activeRowImageField);
            const chosenImage = tileImageOverrides[t.tid] || imageOptions[0] || null;
            const isPickerOpen = imagePickerTile === t.tid;
            return (
              <div
                key={t.tid || t.id}
                draggable
                onDragStart={(e) => {
                  handleDragStart(e, index);
                  e.currentTarget.classList.add("opacity-50", "scale-95");
                }}
                onDragEnd={(e) => {
                  e.currentTarget.classList.remove("opacity-50", "scale-95");
                }}
                onDragOver={(e) => handleDragOver(e, index)}
                onClick={() => {
                  if (isEligible) {
                    setExpandedUtmTile(isActive ? null : t.tid);
                  } else {
                    const sources = Array.isArray(t.source) ? t.source : [];
                    const sourceNames = sources.map((id) => resolveSourceName(id)).filter(Boolean);
                    const label = sourceNames.length ? sourceNames.join(", ") : "Unknown";
                    Swal.fire({
                      icon: "info",
                      title: "UTM Not Applicable",
                      text: `This tile's source is "${label}". UTM deeplink params are only supported for Sony, Hotstar, Sun NXT, and Jojo sources.`,
                      confirmButtonColor: "#3085d6",
                      timer: 4000,
                      timerProgressBar: true,
                    });
                  }
                }}
                className={`flex flex-col gap-2 border py-2 px-3 rounded-md shadow-sm cursor-grab active:cursor-grabbing transition-all duration-300 ease-in-out hover:shadow-md ${isActive ? "border-blue-500 bg-blue-50" : "border-gray-400 bg-slate-200"}`}
              >
                <div className="flex justify-between items-center gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {imageOptions.length > 0 && (
                      <button
                        type="button"
                        title="Choose which image shows for this tile on the page"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImagePickerTile(isPickerOpen ? null : t.tid);
                        }}
                        className={`relative w-14 h-8 shrink-0 rounded overflow-hidden border-2 ${isPickerOpen ? "border-blue-500" : "border-gray-300"}`}
                      >
                        {chosenImage ? (
                          <img
                            src={resolveTileImageUrl(chosenImage, activeRowImageField)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-[9px] text-gray-400">No img</span>
                        )}
                      </button>
                    )}
                    <p className="text-sm text-gray-900 font-semibold truncate">{t.title}</p>
                  </div>
                  <RiDeleteBin6Line
                    className="text-red-600 cursor-pointer hover:scale-110 transition-transform shrink-0"
                    onClick={(e) => { e.stopPropagation(); toggleTileSelection(t); }}
                  />
                </div>

                {isPickerOpen && imageOptions.length > 0 && (
                  <div
                    className="flex flex-wrap gap-2 p-2 bg-white border border-gray-200 rounded cursor-default"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {imageOptions.map((path, i) => {
                      const isChosen = chosenImage === path;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setTileImageOverrides((prev) => ({ ...prev, [t.tid]: path }));
                            setImagePickerTile(null);
                          }}
                          className={`w-14 h-8 rounded overflow-hidden border-2 ${isChosen ? "border-blue-500" : "border-transparent"}`}
                        >
                          <img
                            src={resolveTileImageUrl(path, activeRowImageField)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          </>
        )}

        {/* UTM Parameters — only shown when a sony/hotstar tile is selected */}
        {selectedTiles.some((tile) => isUtmEligibleSource(tile.source)) && (
          <div className="border border-gray-200 rounded-md p-4 bg-white">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Deeplink UTM Parameters</h3>
            <p className="text-xs text-gray-400 mb-3">
              Set defaults below — click a tile above to override per tile.
            </p>

            {/* Row-level defaults */}

              

            {/* Active tile editor */}
            {(() => {
              const activeTile = selectedTiles.find((t) => t.tid === expandedUtmTile);
              if (!activeTile) return null;

              // SunNXT only ever carries a single "source" param, so it gets
              // its own single editable field instead of the generic
              // three-field utm_source/medium/campaign form below. Jojo uses
              // that generic form the same way Sony/Hotstar do.
              const activeSources = Array.isArray(activeTile.source) ? activeTile.source : [];
              const activeSourceNames = activeSources.map((id) => resolveSourceName(id).toLowerCase());
              const isActiveSunNxt = activeSourceNames.some((n) => n.replace(/\s+/g, "").includes("sunnxt"));

              if (isActiveSunNxt) {
                const sourceValue = tileUtmParams[activeTile.tid]?.source ?? "cloudTV";
                return (
                  <div className="mt-4 border-t border-gray-200 pt-4 space-y-3">
                    <p className="text-xs font-semibold text-gray-700">
                      Editing: <span className="text-blue-600">{activeTile.title}</span>
                    </p>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">source</label>
                      <input
                        type="text"
                        value={sourceValue}
                        onChange={(e) => updateTileUtm(activeTile.tid, "source", e.target.value)}
                        className="w-full py-1.5 px-2 text-xs border rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="cloudTV"
                      />
                    </div>
                    <p className="text-xs text-blue-700 break-all font-mono bg-gray-50 p-2 rounded border border-gray-100">
                      ...&amp;source={sourceValue}
                    </p>
                  </div>
                );
              }

              // Jojo falls through to the generic editable panel below,
              // the same way Sony/Hotstar do.
              const params = tileUtmParams[activeTile.tid] || {
                utm_source: formData.utm_source || "cloudtvos",
                utm_medium: formData.utm_medium || "",
                utm_campaign: formData.utm_campaign || "",
              };
              const raw = activeTile.target;
              let baseUrl = null;
              try {
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                baseUrl = Array.isArray(parsed) ? (parsed[0] || null) : (String(parsed) || null);
              } catch {
                baseUrl = typeof raw === "string" ? raw : null;
              }
              const utmParts = [];
if (params.utm_source) utmParts.push(`utm_source=${params.utm_source}`);
if (params.utm_medium) utmParts.push(`utm_medium=${params.utm_medium}`);
if (params.utm_campaign) utmParts.push(`utm_campaign=${params.utm_campaign}`);
              const utmSuffix = utmParts.join("&");
              const preview = baseUrl
                ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${utmSuffix}`
                : `<target_url>${utmSuffix ? `?${utmSuffix}` : ""}`;
              return (
                <div className="mt-4 border-t border-gray-200 pt-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-700">
                    Editing: <span className="text-blue-600">{activeTile.title}</span>
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">utm_source</label>
                      <input
                        type="text"
                        value={params.utm_source}
                        onChange={(e) => updateTileUtm(activeTile.tid, "utm_source", e.target.value)}
                        className="w-full py-1.5 px-2 text-xs border rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="cloudtvos"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">utm_medium</label>
                      <input
                        type="text"
                        value={params.utm_medium}
                        onChange={(e) => updateTileUtm(activeTile.tid, "utm_medium", e.target.value)}
                        className="w-full py-1.5 px-2 text-xs border rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="row title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">utm_campaign</label>
                      <input
                        type="text"
                        value={params.utm_campaign}
                        onChange={(e) => updateTileUtm(activeTile.tid, "utm_campaign", e.target.value)}
                        className="w-full py-1.5 px-2 text-xs border rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="tata_ipl_2026"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 break-all font-mono bg-gray-50 p-2 rounded border border-gray-100">{preview}</p>
                </div>
              );
            })()}
          </div>
        )}

        <div className="text-center sticky bottom-0 bg-gray-100 py-2 rounded-b-md">
          <Button variant="success" type="submit" className="px-5 text-sm">
            Save Row
          </Button>
        </div>
      </form>

      <Modal
        // customClasses="max-w-xl sm:max-w-2xl md:max-w-3xl lg:max-w-6xl w-full mt-0 h-[90vh]"
        customClasses=" !w-screen !max-w-none !h-screen !m-0 !rounded-none"
        showModal={showTilesModal}
        setShowModal={setShowTilesModal}
      >
        <ShowAllTiles
          selectedTiles={selectedTiles}
          setSelectedTiles={setSelectedTiles}
          toggleTileSelection={toggleTileSelection}
          imageField={suitableImageField}
          tileImageOverrides={tileImageOverrides}
          setTileImageOverrides={setTileImageOverrides}
          imagePickerTile={imagePickerTile}
          setImagePickerTile={setImagePickerTile}
          getTileImageOptions={getTileImageOptions}
          resolveTileImageUrl={resolveTileImageUrl}
        />
      </Modal>

      <Modal
        customClasses="!w-screen !max-w-none !h-screen !m-0 !rounded-none"
        showModal={showPreviewModal}
        setShowModal={setShowPreviewModal}
      >
        {previewTile && <MovieTile data={[previewTile]} />}
      </Modal>

      {selectedTiles.length > 0 && (showTilesModal || showPreviewModal) && (
        <button
          type="button"
          onClick={() => {
            setShowTilesModal(false);
            setShowPreviewModal(false);
            pageContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="fixed bottom-9 right-6 z-50 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg transition-all"
        >
          ↑ Continue
        </button>
      )}
    </div>
  );
}

export default RowForm;